import { faChainBroken, faPauseCircle, faPlayCircle, faQuestionCircle, faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment } from "react";
import { Menu, MenuItem, Option, TypeaheadLabelKey } from "react-bootstrap-typeahead";
import { checkForMatch } from "../utils/misc";
import { CampaignRecord, CampaignStatus } from "../utils/types";
import Autocomplete from "./library/Autocomplete";
import { isAllowedFilterField } from "../utils/UserHelper";

function CampaignsAutocomplete(props: any) {

  const formatCampaign = function (campaign: CampaignRecord) {
    const allCampaigns = campaign.campaignId === "***";
    const untracked = !allCampaigns && campaign.campaignId && campaign.campaignId.startsWith("*");
    const allDemand = campaign.campaignSource === "***" && !untracked;

    let campaignSource = allDemand ? "All Demand" : campaign.campaignSource;
    campaignSource = untracked && !allCampaigns ? "<untracked>" : campaignSource;

    const campaignId = allCampaigns ? "All Campaigns" : campaign.campaignId;
    let productId = campaign.productId;
    if ( untracked || allCampaigns ) {
      productId = "All Products";
    }

    if (campaign.campaignSource) {
      campaignSource = campaign.campaignSource;
    }
    const campaignSourceAlias = campaign.campaignSourceAlias ? campaign.campaignSourceAlias : null;

    return {
      allCampaigns, untracked, allDemand, campaignSource, campaignSourceAlias, campaignId, productId
    };
  }

  /**
   * Custom renderMenuItem to allow for custom padding on the menu items (less padding),
   * while still retaining the default pagination behavior. 
   * This really should be abstracted into Autocomplete.jsx, but right now it's tied to the
   * data-types for this particular implementation.
   */
  const renderMenuItemForCampaignsPermissions = (option: Option, position: number) => {

    const menuItemProps = {
      disabled: !!(option as any)['disabled'],
      option,
      position,
    };

    if ( (option as any)['paginationOption'] ) {
      return (
        <Fragment key="pagination-option-divider">
          <Menu.Divider />
          <MenuItem
            {...menuItemProps}
            className="rbt-menu-pagination-option"
            label={
              "More"
            }>
            "More"
          </MenuItem>
        </Fragment>
      );
    }

    return (
      <MenuItem {...menuItemProps} key={position} style={{paddingLeft: "1em", paddingRight: "1em"}}>
        {renderMenuItemChildrenForCampaignsPermissions((option as CampaignRecord), props, position)}
      </MenuItem>
    );
  };

  const renderMenuItemChildrenForCampaignsPermissions = function (campaign: CampaignRecord, obj : any, index : number) {
    let statusIcon = null;
    let color = "#000";
    let tooltip = null;
    let menuItem = null;

    switch ( campaign.campaignStatus ) {
      case CampaignStatus.Paused:
        statusIcon = <FontAwesomeIcon icon={faPauseCircle} fixedWidth></FontAwesomeIcon>;
        color="#ccc";
        tooltip = "Paused. Campaign is currently (today) paused in AppTap system.";
        break;
      case CampaignStatus.Active:
        statusIcon = <FontAwesomeIcon icon={faPlayCircle} fixedWidth></FontAwesomeIcon>;
        color="green";
        tooltip = "Active. Campaign is currently (today) active in AppTap system.";
        break;
      default: // unknown and not specified - same icon
        color="#ccc";
        tooltip = "Unknown. Campaign is currently not defined in AppTap system.";
        statusIcon = <FontAwesomeIcon icon={faQuestionCircle} fixedWidth></FontAwesomeIcon>
        break;
    }

    if ( !campaign.campaignId ) {
      campaign.campaignId = "***";
    }

    const { allCampaigns,
            untracked,
            campaignSource,
            campaignSourceAlias,
            campaignId,
            productId 
          }:{
            allCampaigns: boolean,
            untracked: boolean,
            campaignSource: string,
            campaignSourceAlias: string,
            campaignId: string,
            productId: string
          } = formatCampaign(campaign);

    if ( untracked ) {
      statusIcon = <FontAwesomeIcon icon={faChainBroken} fixedWidth></FontAwesomeIcon>
      tooltip = "Untracked click error message / status code.";
    } else if ( allCampaigns ) {
      statusIcon = <FontAwesomeIcon icon={faStar} fixedWidth></FontAwesomeIcon>
      tooltip = "All campaigns and all products for this demand will be returned."
    }


    menuItem = (
      <div style={{display: "flex", flexDirection: "row", width: "100%", fontWeight: allCampaigns ? "bold" : "normal", marginLeft: "-10px"}}>
        <div style={{minWidth: "2em", textAlign: "center", color: color, fontSize: "120%"}} title={tooltip}>{statusIcon}</div>

        <div style={{width: "60%", display: "flex", flexDirection: "column", flexWrap: "wrap"}}>
          <div style={{display: "flex", flexDirection: "row", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginRight: "5px",
              WebkitLineClamp: 2, WebkitBoxOrient: "vertical"}}>
            {campaignSourceAlias}
          </div>
          <div style={{overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginRight: "5px",
              WebkitLineClamp: 2, WebkitBoxOrient: "vertical", display: "-webkit-inline-box", fontWeight: "normal", fontStyle: "italic"}}>
            {campaignSource}
          </div>
        </div>
        <div style={{width: "30%", display: "flex", flexDirection: "column", flexWrap: "wrap"}}>
          <div>{productId}</div>      
          <div>{campaignId}</div>
        </div>
      </div>);

     return menuItem;
  }

  const filterByForCampaignsPermissions = function(campaign: CampaignRecord, props: any) {
    // when there's no search string, only show campaign sources (campaignID is wildcard)
    if ( props.text == null || (props.text as string).length === 0 ) {
      return campaign.campaignId === "***";
    }

    const searchString = (props.text as string).trim().toLowerCase();
    const searchStrings:string[] = searchString.split(" ");

    const { campaignSource, productId }: { campaignSource: string, productId: string }  = formatCampaign(campaign as CampaignRecord);

    const text = [
      campaign.labelKey,
      campaignSource,
      productId
    ].join(" ").toLowerCase();
    return checkForMatch(searchStrings, text);
  }
  
  const renderMenuItemForAliasPermissions = (option: Option, position: number) => {
    const menuItemProps = {
      disabled: !!(option as any)['disabled'],
      option,
      position,
    };

    if ( (option as any)['paginationOption'] ) {
      return (
        <Fragment key="pagination-option-divider">
          <Menu.Divider />
          <MenuItem
            {...menuItemProps}
            className="rbt-menu-pagination-option"
            label={
              "More"
            }>
            "More"
          </MenuItem>
        </Fragment>
      );
    }

    return (
      <MenuItem {...menuItemProps} key={position} style={{paddingLeft: "1em", paddingRight: "1em"}}>
        {renderMenuItemChildrenForAliasPermissions((option as CampaignRecord), props, position)}
      </MenuItem>
    );
  };

  const renderMenuItemChildrenForAliasPermissions = function (campaignRecord: CampaignRecord, obj : any, index : number) {
    return (
      <div style={{display: "flex", flexDirection: "row", flexWrap: "nowrap", width: "100%", fontWeight: "bold", marginLeft: "-10px"}}>
        <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap", width: "100%"}}>
        <div style={{flexShrink: 1, flexGrow: 1, width: "40%", minWidth: "10em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginRight: "5px",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical", display: "-webkit-inline-box"
                  }}>{campaignRecord.campaignSourceAlias }</div>
          <div style={{flexShrink: 1, flexGrow: 1, width: "20%", minWidth: "6em", overflow: "hidden", textOverflow: "ellipsis"}}>All Campaigns</div>
          <div style={{flexShrink: 1, flexGrow: 1, width: "20%", minWidth: "8em", overflow: "hidden", textOverflow: "ellipsis"}}>All Products</div>      
        </div>
      </div>);
  }


  const filterByForAliasPermissions = function(campaignRecord: CampaignRecord, props: any) {
    const searchString = (props.text as string).trim().toLowerCase();
    const searchStrings:string[] = searchString.split(" ");

    const text = [
      campaignRecord.campaignSourceAlias
    ].join(" ").toLowerCase();
    return checkForMatch(searchStrings, text);
  }  

  props.options.sort((a:any, b:any) => {
    if (a.campaignId === "***" && b.campaignId !== "***") return -1;
    if (a.campaignId !== "***" && b.campaignId === "***") return 1;
    return 0;
  });

  return (
    <Autocomplete 
      id="campaignsAutocomplete1" 
      placeholder="Campaigns" 
      reactKey="campaignsAutocomplete2"
      onChange={props.onChange} 
      options={props.options} 
      selected={props.selected}
      labelKey={'labelKey' as TypeaheadLabelKey<string>}
      renderMenuItem={props.hasCampaignsPermissions ? renderMenuItemForCampaignsPermissions : renderMenuItemForAliasPermissions}
      renderMenuItemChildren={props.hasCampaignsPermissions ? renderMenuItemChildrenForCampaignsPermissions : renderMenuItemChildrenForAliasPermissions}
      filterBy={props.hasCampaignsPermissions ? filterByForCampaignsPermissions : filterByForAliasPermissions}
      reactRef={props.reactRef}
      disabled={props.disabled}
      useIncludeExclude={isAllowedFilterField("campaignsExclude")}
      defaultIncludeExclude={props.defaultIncludeExclude}
      includeExcludeOnChange={props.includeExcludeOnChange}
      multiple
    />
  );
}

export default CampaignsAutocomplete;
